import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, Coffee, TrendingUp, Sparkles, PauseCircle, PlayCircle, Megaphone, Copy, Eye, EyeOff, Globe, KeyRound, Check, Trash2, AlertTriangle, User, FileText, Save, Plus, UserPlus, Lock, ExternalLink, MessageCircle, MailX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatMoney } from "@/lib/currency";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MODULES = [
  { key: "cafe", label: "Café" },
  { key: "fitness", label: "Fitness" },
  { key: "retail", label: "Retail" },
  { key: "retreat", label: "Retreat" },
  { key: "marketing", label: "Marketing" },
  { key: "ai", label: "AI" },
  { key: "growth", label: "Growth Engine" },
  { key: "automation", label: "Automation" },
  { key: "whitelabel", label: "White Label" },
  { key: "bi", label: "BI" },
];

const PLANS = ["trial", "basic", "pro", "enterprise"];
const STATUSES = ["active", "paused", "cancelled"];

/**
 * Parent Dashboard — Super Admin only view spanning every workspace on
 * the platform. Owned by role='super_admin'; the /admin route + sidebar
 * links are conditionally rendered in App.js and DashboardShell.
 */
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/tenants"),
      ]);
      setStats(s);
      setTenants(t);
    } catch {
      toast.error("Could not load parent dashboard.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleModule = async (tenant, moduleKey) => {
    const current = new Set(tenant.modules || []);
    if (current.has(moduleKey)) current.delete(moduleKey);
    else current.add(moduleKey);
    const modules = Array.from(current);
    try {
      const { data } = await api.patch(`/admin/tenants/${tenant.id}/modules`, { modules });
      setTenants((prev) => prev.map((t) => (t.id === tenant.id ? { ...t, ...data } : t)));
      toast.success(`${tenant.name} · modules updated`);
    } catch {
      toast.error("Failed to toggle module.");
    }
  };

  const updatePlan = async (tenant, field, value) => {
    try {
      const { data } = await api.patch(`/admin/tenants/${tenant.id}/plan`, { [field]: value });
      setTenants((prev) => prev.map((t) => (t.id === tenant.id ? { ...t, ...data } : t)));
      toast.success(`${tenant.name} · ${field} = ${value}`);
    } catch {
      toast.error(`Failed to update ${field}.`);
    }
  };

  const deleteTenant = async (tenant, confirmName) => {
    try {
      const { data } = await api.delete(`/admin/tenants/${tenant.id}`, {
        data: { confirm_name: confirmName },
      });
      setTenants((prev) => prev.filter((t) => t.id !== tenant.id));
      // Also refresh platform stats so KPIs decrement
      try {
        const { data: s } = await api.get("/admin/stats");
        setStats(s);
      } catch {}
      const removed = Object.values(data.records_deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(
        `${tenant.name} deleted · ${data.users_deleted} user${data.users_deleted === 1 ? "" : "s"} + ${removed} record${removed === 1 ? "" : "s"} removed`,
      );
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      return false;
    }
  };

  if (loading) {
    return <div className="text-muted-foreground text-sm py-24 text-center">Loading platform data…</div>;
  }

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      {/* ---------- Header ---------- */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">Parent dashboard</div>
          <h1 className="font-display text-4xl leading-tight">
            Every workspace, at a glance.
          </h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Super-admin console. Toggle modules per client, change subscription plans, and see
            aggregated numbers across the entire Kairosa platform.
          </p>
        </div>
        <Link to="/app/admin/crm" data-testid="admin-crm-link">
          <Button variant="outline" className="rounded-full gap-2">
            <Megaphone className="h-4 w-4" /> Open Sales CRM
          </Button>
        </Link>
        <Link to="/app/admin/whatsapp" data-testid="admin-whatsapp-link">
          <Button variant="outline" className="rounded-full gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp Log
          </Button>
        </Link>
        <Link to="/app/admin/whatsapp/templates" data-testid="admin-whatsapp-templates-link">
          <Button variant="outline" className="rounded-full gap-2">
            <FileText className="h-4 w-4" /> WA Templates
          </Button>
        </Link>
        <Link to="/app/admin/unsubscribes" data-testid="admin-unsubscribes-link">
          <Button variant="outline" className="rounded-full gap-2">
            <MailX className="h-4 w-4 text-destructive" /> Unsubscribes
          </Button>
        </Link>
      </div>

      {/* ---------- Platform Admin Profile ---------- */}
      <PlatformAdminProfile />

      {/* ---------- Platform Admin Resources ---------- */}
      <AdminResources />

      {/* ---------- Platform-wide KPIs ---------- */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            testid="admin-kpi-tenants"
            icon={<Building2 className="h-4 w-4" />}
            label="Workspaces"
            value={stats.tenants.total}
            hint={`${stats.tenants.active} active · ${stats.tenants.paused} paused`}
          />
          <KpiCard
            testid="admin-kpi-users"
            icon={<Users className="h-4 w-4" />}
            label="Users"
            value={stats.users_total}
          />
          <KpiCard
            testid="admin-kpi-orders"
            icon={<Coffee className="h-4 w-4" />}
            label="Café orders (platform)"
            value={stats.orders_total}
          />
          <KpiCard
            testid="admin-kpi-revenue"
            icon={<TrendingUp className="h-4 w-4" />}
            label="Café revenue (platform)"
            value={formatMoney(stats.revenue_total || 0, undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          />
        </div>
      )}

      {/* ---------- Module attach rate ---------- */}
      {stats?.module_attach_rate && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="font-display text-xl">Module attach rate</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {MODULES.map((m) => {
              const count = stats.module_attach_rate[m.key] || 0;
              const pct = stats.tenants.total ? Math.round((count / stats.tenants.total) * 100) : 0;
              return (
                <div
                  key={m.key}
                  className="rounded-lg border border-border/70 bg-secondary/40 p-3"
                  data-testid={`admin-module-attach-${m.key}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{m.label}</div>
                  <div className="font-display text-lg mt-1">{pct}%</div>
                  <div className="text-xs text-muted-foreground">{count} / {stats.tenants.total}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------- Workspace list ---------- */}
      <div className="rounded-2xl border border-border bg-card">
        <div className="p-5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl">Workspaces</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Toggle a module to grant or revoke access for that workspace instantly.
            </p>
          </div>
          <CreateWorkspaceButton onCreated={load} />
        </div>
        <div className="divide-y divide-border">
          {tenants.map((t) => (
            <div key={t.id} data-testid={`admin-tenant-row-${t.id}`} className="p-5 space-y-3">
              <div className="flex flex-wrap items-start gap-4">
                <div className="min-w-[220px] flex-1">
                  <div className="font-display text-lg flex items-center gap-2 flex-wrap">
                    {t.name}
                    <TenantCredsPopover tenant={t} onSaved={load} />
                    <AddOwnerButton tenant={t} onSaved={load} />
                    <TenantVaultButton tenant={t} onSaved={load} />
                    {t.custom_domain && t.domain_verified && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-primary"
                        title={t.custom_domain}
                        data-testid={`admin-tenant-domain-badge-${t.id}`}
                      >
                        <Globe className="h-3 w-3" /> {t.custom_domain}
                      </span>
                    )}
                    {t.is_demo && (
                      <span className="rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-accent" data-testid={`admin-tenant-demo-badge-${t.id}`}>
                        Demo
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t.slug} · {t.industry || "wellness"} · {t.counts.users} user{t.counts.users === 1 ? "" : "s"}
                    {t.created_at && (
                      <> · <span title={t.created_at}>Created {formatCreatedAt(t.created_at)}</span></>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <StatChip label="Orders" value={t.counts.orders} />
                  <StatChip label="Clients" value={t.counts.clients} />
                  <StatChip label="Revenue" value={formatMoney(t.revenue_total || 0, undefined, { maximumFractionDigits: 0 })} />
                </div>
                <div className="flex items-center gap-2">
                  <Select value={t.plan || "trial"} onValueChange={(v) => updatePlan(t, "plan", v)}>
                    <SelectTrigger data-testid={`admin-plan-${t.id}`} className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={t.status || "active"} onValueChange={(v) => updatePlan(t, "status", v)}>
                    <SelectTrigger data-testid={`admin-status-${t.id}`} className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          <span className="inline-flex items-center gap-1.5">
                            {s === "paused" ? <PauseCircle className="h-3 w-3" /> : <PlayCircle className="h-3 w-3" />}
                            {s}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <DeleteTenantButton tenant={t} onDelete={deleteTenant} />
                </div>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
                {MODULES.map((m) => {
                  const on = (t.modules || []).includes(m.key);
                  return (
                    <label
                      key={m.key}
                      data-testid={`admin-module-toggle-${t.id}-${m.key}`}
                      className="inline-flex items-center gap-2 text-xs cursor-pointer select-none"
                    >
                      <Switch checked={on} onCheckedChange={() => toggleModule(t, m.key)} />
                      <span className={on ? "text-foreground" : "text-muted-foreground"}>{m.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, hint, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2.5 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-semibold">{value}</span>
    </span>
  );
}

/**
 * Hover / click popover per tenant row exposing owner email + demo password
 * (only if is_demo) + custom-domain attach. Super-admin can flip is_demo,
 * rotate the demo password (also updates the owner's bcrypt hash), attach a
 * verified custom domain.
 */
function TenantCredsPopover({ tenant, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pwVisible, setPwVisible] = useState(false);
  const [isDemo, setIsDemo] = useState(!!tenant.is_demo);
  const [demoPw, setDemoPw] = useState(tenant.demo_password || "");
  const [domain, setDomain] = useState(tenant.custom_domain || "");
  const [busy, setBusy] = useState(false);

  const copy = async (val, what) => {
    try {
      await navigator.clipboard.writeText(val || "");
      toast.success(`${what} copied`);
    } catch { toast.error("Copy failed"); }
  };

  const saveDemo = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${tenant.id}/demo`, {
        is_demo: isDemo,
        demo_password: isDemo ? demoPw : undefined,
      });
      toast.success(isDemo ? "Demo credentials saved" : "Demo flag cleared");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const saveDomain = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${tenant.id}/custom-domain`, {
        custom_domain: domain.trim(),
        verified: true,
      });
      toast.success(domain.trim() ? "Domain attached" : "Domain removed");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`admin-tenant-creds-btn-${tenant.id}`}
          title="Show credentials & custom domain"
          className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
        >
          <KeyRound className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-4 space-y-4"
        data-testid={`admin-tenant-creds-popover-${tenant.id}`}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Owner login</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 truncate text-sm font-mono" data-testid={`admin-tenant-owner-email-${tenant.id}`}>
              {tenant.owner_email || "—"}
            </div>
            {tenant.owner_email && (
              <button onClick={() => copy(tenant.owner_email, "Email")} className="p-1 rounded hover:bg-muted text-muted-foreground" title="Copy email">
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={isDemo} onCheckedChange={setIsDemo} data-testid={`admin-tenant-demo-toggle-${tenant.id}`} />
            <span className="text-xs font-semibold">Demo workspace</span>
          </label>
          {isDemo && (
            <>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Demo password (stored plaintext)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type={pwVisible ? "text" : "password"}
                  value={demoPw}
                  onChange={(e) => setDemoPw(e.target.value)}
                  placeholder="e.g. Demo123!"
                  className="font-mono text-sm h-8"
                  data-testid={`admin-tenant-demo-pw-${tenant.id}`}
                />
                <button onClick={() => setPwVisible((v) => !v)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title={pwVisible ? "Hide" : "Show"} data-testid={`admin-tenant-demo-pw-toggle-${tenant.id}`}>
                  {pwVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
                {demoPw && (
                  <button onClick={() => copy(demoPw, "Password")} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Copy password">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground leading-relaxed">
                Rotating this password also updates the owner's bcrypt hash so
                <code className="mx-1">POST /auth/login</code>
                accepts it immediately.
              </div>
            </>
          )}
          <Button size="sm" className="w-full h-7 text-xs rounded-full" onClick={saveDemo} disabled={busy} data-testid={`admin-tenant-demo-save-${tenant.id}`}>
            <Check className="h-3 w-3 mr-1" /> Save demo settings
          </Button>
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold">
            <Globe className="h-3.5 w-3.5 text-primary" /> Custom domain (white-label)
          </div>
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="studio-a.com"
            className="text-sm h-8"
            data-testid={`admin-tenant-domain-input-${tenant.id}`}
          />
          <div className="text-[10px] text-muted-foreground leading-relaxed">
            Point the domain's DNS to Emergent (CNAME via Entri), then attach it here.
            Visitors to that host see the tenant's branded app.
          </div>
          <Button size="sm" variant="outline" className="w-full h-7 text-xs rounded-full" onClick={saveDomain} disabled={busy} data-testid={`admin-tenant-domain-save-${tenant.id}`}>
            <Check className="h-3 w-3 mr-1" /> {domain.trim() ? "Attach & verify" : "Detach domain"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Danger-zone button. Opens a modal that forces the super-admin to type the
 * workspace's exact name before we call DELETE /api/admin/tenants/{id}.
 * The `kairos-platform` tenant is protected server-side; we also grey the
 * button here so it's obvious it can't be nuked.
 */
function DeleteTenantButton({ tenant, onDelete }) {
  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const isPlatform = tenant.slug === "kairos-platform";
  const canDelete = !isPlatform && confirmName.trim() === tenant.name;

  const handleDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    const ok = await onDelete(tenant, confirmName.trim());
    setBusy(false);
    if (ok) {
      setOpen(false);
      setConfirmName("");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmName(""); }}>
      <AlertDialogTrigger asChild>
        <button
          data-testid={`admin-tenant-delete-btn-${tenant.id}`}
          disabled={isPlatform}
          title={isPlatform ? "Platform workspace — protected" : `Delete ${tenant.name}`}
          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-destructive/40 text-destructive/80 hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`admin-tenant-delete-dialog-${tenant.id}`}>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 shrink-0 rounded-full bg-destructive/15 grid place-items-center">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
            <AlertDialogTitle className="font-display text-2xl">
              Delete this workspace?
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2 space-y-2">
            <div>
              You are about to <b className="text-destructive">permanently delete</b> the workspace
              <span className="font-semibold text-foreground"> {tenant.name}</span> (slug:
              <code className="mx-1 text-xs">{tenant.slug}</code>).
            </div>
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-foreground/90">
              This will erase:
              <ul className="mt-1 ml-4 list-disc space-y-0.5 text-xs">
                <li>{tenant.counts?.users ?? 0} user account{tenant.counts?.users === 1 ? "" : "s"}</li>
                <li>{tenant.counts?.orders ?? 0} café order{tenant.counts?.orders === 1 ? "" : "s"} + all menu items</li>
                <li>{tenant.counts?.clients ?? 0} fitness client{tenant.counts?.clients === 1 ? "" : "s"} + workouts + exercises</li>
                <li>All Growth Engine leads, roastery inventory, and any other tenant-scoped data</li>
              </ul>
              <div className="mt-2 text-xs text-destructive font-medium">This action cannot be undone.</div>
            </div>
            <div className="pt-1">
              <Label className="text-xs">
                Type <code className="font-mono text-foreground">{tenant.name}</code> to confirm:
              </Label>
              <Input
                data-testid={`admin-tenant-delete-confirm-input-${tenant.id}`}
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={tenant.name}
                autoComplete="off"
                className="mt-1.5 font-mono"
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`admin-tenant-delete-cancel-${tenant.id}`} disabled={busy}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`admin-tenant-delete-confirm-${tenant.id}`}
            disabled={!canDelete || busy}
            onClick={(e) => { e.preventDefault(); handleDelete(); }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {busy ? "Deleting…" : "Delete workspace"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


/* -------------------------------------------------------------------- */
/*   formatCreatedAt — friendly relative-time formatter for the row     */
/*   sub-line. Falls back to a plain locale date when it's more than    */
/*   a month old (relative gets awkward past that).                     */
/* -------------------------------------------------------------------- */
function formatCreatedAt(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now - then;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 60 * 1000) return "just now";
  if (diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 1000))} min ago`;
  if (diffMs < day) return `${Math.floor(diffMs / (60 * 60 * 1000))} h ago`;
  if (diffMs < 30 * day) return `${Math.floor(diffMs / day)} d ago`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}


/* -------------------------------------------------------------------- */
/*   CreateWorkspaceButton — inline dialog that provisions a brand new  */
/*   client workspace + seeds the initial owner user in BUILD stage.    */
/* -------------------------------------------------------------------- */
function CreateWorkspaceButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", industry: "wellness",
    owner_email: "", owner_name: "", owner_password: "",
    modules: ["core", "growth", "marketing"],
    plan: "trial", status: "paused",
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  // Auto-slug from name unless the user has typed a slug already
  const autoSlug = form.name
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
  const effectiveSlug = form.slug.trim() || autoSlug;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.owner_email.trim() || form.owner_password.length < 8) {
      toast.error("Fill name, owner email, and a password (8+ chars)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/tenants", { ...form, slug: effectiveSlug });
      toast.success("Workspace created in BUILD stage · flip to Active when ready");
      setOpen(false);
      setForm({
        name: "", slug: "", industry: "wellness",
        owner_email: "", owner_name: "", owner_password: "",
        modules: ["core", "growth", "marketing"],
        plan: "trial", status: "paused",
      });
      onCreated && onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="rounded-full gap-1.5" data-testid="admin-new-workspace-btn">
        <Plus className="h-4 w-4" /> New workspace
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => !busy && setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-background border border-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-new-workspace-dialog">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display text-2xl">New client workspace</div>
              <button type="button" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-foreground/10" aria-label="Close">
                <Plus className="h-4 w-4 rotate-45" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Provisions a fresh workspace in <b>BUILD</b> stage (status=paused). You configure branding, modules,
              and integrations, then flip status to <b>Active</b> to hand it over.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Business name *</Label>
                <Input required value={form.name} onChange={upd("name")} placeholder="e.g. Riverside Fitness Studio" className="mt-1.5" data-testid="admin-new-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Slug (URL)</Label>
                <Input value={form.slug} onChange={upd("slug")} placeholder={autoSlug || "riverside-fitness"} className="mt-1.5" data-testid="admin-new-slug" />
                <div className="text-[10px] text-muted-foreground mt-1">Used for {effectiveSlug || "…"}.kairosa.io</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Industry</Label>
                <select value={form.industry} onChange={upd("industry")} className="mt-1.5 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" data-testid="admin-new-industry">
                  {["wellness", "cafe", "fitness", "retreat", "retail", "hybrid"].map((i) => (
                    <option key={i} value={i} className="capitalize">{i}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 border-t border-border pt-3 mt-1">
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Owner login *</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  These credentials will belong to the client (or you, if operating for them).
                </p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Owner email *</Label>
                <Input required type="email" value={form.owner_email} onChange={upd("owner_email")} placeholder="founder@customer.com" className="mt-1.5" data-testid="admin-new-owner-email" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Owner name</Label>
                <Input value={form.owner_name} onChange={upd("owner_name")} placeholder="Jane Founder" className="mt-1.5" data-testid="admin-new-owner-name" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Temporary password * (8+ chars)</Label>
                <Input required type="text" value={form.owner_password} onChange={upd("owner_password")} placeholder="LetThemChangeThis!" className="mt-1.5 font-mono" data-testid="admin-new-owner-password" />
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="admin-new-workspace-submit">
                {busy ? "Creating…" : "Create workspace"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}


/* -------------------------------------------------------------------- */
/*   AddOwnerButton — small icon-button on every workspace row that     */
/*   opens a popover to add an additional business_owner user.          */
/* -------------------------------------------------------------------- */
function AddOwnerButton({ tenant, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const submit = async () => {
    if (!form.email.trim() || form.password.length < 8) {
      toast.error("Enter email and password (8+ chars)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${tenant.id}/users`, form);
      toast.success(`Owner ${form.email} added`);
      setForm({ email: "", name: "", password: "" });
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add owner failed");
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-muted-foreground hover:text-primary transition"
          title="Add owner"
          data-testid={`admin-add-owner-btn-${tenant.id}`}
        >
          <UserPlus className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 space-y-2" data-testid={`admin-add-owner-popover-${tenant.id}`}>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Invite owner</div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Email *</Label>
          <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} type="email" className="mt-1 h-8 text-xs" data-testid={`admin-add-owner-email-${tenant.id}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Name (optional)</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 h-8 text-xs" data-testid={`admin-add-owner-name-${tenant.id}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Temp password *</Label>
          <Input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} type="text" className="mt-1 h-8 text-xs font-mono" data-testid={`admin-add-owner-pw-${tenant.id}`} />
        </div>
        <Button size="sm" className="w-full h-7 rounded-full text-xs" onClick={submit} disabled={busy} data-testid={`admin-add-owner-save-${tenant.id}`}>
          {busy ? "Adding…" : "Add owner"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}


/* -------------------------------------------------------------------- */
/*   TenantVaultButton — Fernet-encrypted credential vault popover.     */
/*   Shows a masked view; PATCHing a field ciphers the plaintext        */
/*   before storing. Never returns plaintext back to the UI.            */
/* -------------------------------------------------------------------- */
const VAULT_FIELDS = [
  { key: "razorpay_key_id",     label: "Razorpay Key ID",      placeholder: "rzp_live_…" },
  { key: "razorpay_key_secret", label: "Razorpay Key Secret",  placeholder: "••••" },
  { key: "gmail_email",         label: "Gmail address",        placeholder: "outreach@customer.com" },
  { key: "gmail_app_password",  label: "Gmail app password",   placeholder: "xxxx xxxx xxxx xxxx" },
  { key: "serper_api_key",      label: "Serper API key",       placeholder: "abc…" },
  { key: "stripe_secret",       label: "Stripe secret",        placeholder: "sk_live_…" },
  { key: "stripe_publishable",  label: "Stripe publishable",   placeholder: "pk_live_…" },
  { key: "notes",               label: "Notes (free-text)",    placeholder: "Anything the operator should know…", textarea: true },
];

function TenantVaultButton({ tenant, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});   // { field: newValue }

  const stored = tenant.vault || {};
  const storedCount = Object.values(stored).filter(Boolean).length;

  const submit = async () => {
    const payload = Object.fromEntries(
      Object.entries(drafts).filter(([, v]) => typeof v === "string" && v.length > 0)
    );
    if (!Object.keys(payload).length) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${tenant.id}/vault`, payload);
      toast.success("Vault updated");
      setDrafts({});
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vault update failed");
    } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-muted-foreground hover:text-amber-600 transition"
          title={storedCount ? `Vault · ${storedCount} secret${storedCount === 1 ? "" : "s"} stored` : "Operate-for-client vault"}
          data-testid={`admin-vault-btn-${tenant.id}`}
        >
          <Lock className="h-4 w-4" />
          {storedCount > 0 && (
            <span className="ml-0.5 text-[10px] text-amber-600 font-mono">{storedCount}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] p-3 space-y-3 max-h-[70vh] overflow-y-auto" data-testid={`admin-vault-popover-${tenant.id}`}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Operate-for-client vault</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Client-shared credentials, Fernet-encrypted at rest. Fields left blank stay as-is. Type a new value to overwrite.
          </div>
        </div>
        {VAULT_FIELDS.map((f) => {
          const masked = stored[f.key];
          return (
            <div key={f.key}>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center justify-between">
                <span>{f.label}</span>
                {masked && <span className="text-[10px] text-emerald-600 font-mono">{masked}</span>}
              </Label>
              {f.textarea ? (
                <textarea
                  rows={3}
                  value={drafts[f.key] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs"
                  data-testid={`admin-vault-${f.key}-${tenant.id}`}
                />
              ) : (
                <Input
                  value={drafts[f.key] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="mt-1 h-8 text-xs font-mono"
                  data-testid={`admin-vault-${f.key}-${tenant.id}`}
                />
              )}
            </div>
          );
        })}
        <Button size="sm" className="w-full h-7 rounded-full text-xs" onClick={submit} disabled={busy} data-testid={`admin-vault-save-${tenant.id}`}>
          {busy ? "Saving…" : "Save vault"}
        </Button>
      </PopoverContent>
    </Popover>
  );
}




/* -------------------------------------------------------------------- */
/*   Platform Admin Profile — editable identity for the super admin.    */
/*   The saved `name` flows through /auth/me and pre-fills every        */
/*   "Your name" field (AI Outreach Generator, replies, etc).           */
/* -------------------------------------------------------------------- */

function PlatformAdminProfile() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", title: "", avatar_url: "" });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
      setForm({
        name: data?.name || "",
        email: data?.email || "",
        title: data?.title || "",
        avatar_url: data?.avatar_url || "",
      });
    } catch {
      toast.error("Could not load your profile");
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = !!me && (
    form.name !== (me.name || "") ||
    form.email !== (me.email || "") ||
    form.title !== (me.title || "") ||
    form.avatar_url !== (me.avatar_url || "")
  );

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", form);
      setMe((prev) => ({ ...prev, ...data }));
      toast.success("Profile updated · this name will now appear everywhere");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changePwd = async () => {
    if (pwd.next !== pwd.confirm) { toast.error("New passwords don't match"); return; }
    if (pwd.next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password updated");
      setPwd({ current: "", next: "", confirm: "" });
      setPwdOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change failed");
    } finally {
      setPwdSaving(false);
    }
  };

  if (!me) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="admin-profile-card">
      <div className="flex items-center gap-2 mb-4">
        <User className="h-4 w-4 text-primary" />
        <h2 className="font-display text-xl">Platform Admin Profile</h2>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
          · signed in as {me.role || "super_admin"}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Your name</Label>
          <Input
            data-testid="admin-profile-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Sanbhan Bhandari"
            className="mt-1.5"
          />
          <div className="text-[10px] text-muted-foreground mt-1">
            This flows into every "Your name" field across the platform (AI Outreach, replies, activity log).
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Title / role display</Label>
          <Input
            data-testid="admin-profile-title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Founder · The Jay Project"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Email (login)</Label>
          <Input
            data-testid="admin-profile-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Avatar URL (optional)</Label>
          <Input
            data-testid="admin-profile-avatar"
            value={form.avatar_url}
            onChange={(e) => setForm((f) => ({ ...f, avatar_url: e.target.value }))}
            placeholder="https://…"
            className="mt-1.5"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 pt-3 border-t border-border">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-full gap-1.5"
          data-testid="admin-profile-save"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save profile"}
        </Button>
        <Button
          variant="outline"
          className="rounded-full gap-1.5"
          onClick={() => setPwdOpen((v) => !v)}
          data-testid="admin-profile-change-pwd-toggle"
        >
          <KeyRound className="h-3.5 w-3.5" /> Change password
        </Button>
        {dirty && <span className="text-[11px] text-amber-600 ml-auto">Unsaved changes</span>}
      </div>

      {pwdOpen && (
        <div className="mt-4 grid md:grid-cols-3 gap-3 rounded-lg border border-dashed border-border p-4 bg-secondary/30" data-testid="admin-pwd-panel">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Current password</Label>
            <Input type="password" value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))} className="mt-1.5" data-testid="admin-pwd-current" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">New password</Label>
            <Input type="password" value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))} className="mt-1.5" data-testid="admin-pwd-next" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Confirm new password</Label>
            <Input type="password" value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} className="mt-1.5" data-testid="admin-pwd-confirm" />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button
              onClick={changePwd}
              disabled={pwdSaving || !pwd.current || !pwd.next}
              className="rounded-full gap-1.5"
              size="sm"
              data-testid="admin-pwd-save"
            >
              {pwdSaving ? "Updating…" : "Update password"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------- */
/*   Admin Resources — quick-download links to internal playbooks       */
/*   (the whitelabel guide, in the first shipment).                     */
/* -------------------------------------------------------------------- */

function AdminResources() {
  const docs = [
    {
      key: "whitelabel",
      title: "Kairosa Whitelabel Playbook",
      blurb: "Step-by-step guide for using Kairosa as a SaaS to onboard whitelabeled customers.",
      pdf: "/api/static/docs/kairosa_whitelabel_playbook.pdf",
      docx: "/api/static/docs/kairosa_whitelabel_playbook.docx",
    },
  ];
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="admin-resources-card">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-primary" />
        <h2 className="font-display text-xl">Admin Resources</h2>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
          · internal reference
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {docs.map((d) => (
          <div key={d.key} className="rounded-lg border border-border/70 bg-secondary/30 p-4" data-testid={`admin-doc-${d.key}`}>
            <div className="font-medium text-sm">{d.title}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{d.blurb}</div>
            <div className="flex gap-2 mt-3">
              <a href={d.pdf} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="rounded-full gap-1.5" data-testid={`admin-doc-${d.key}-pdf`}>
                  <FileText className="h-3.5 w-3.5" /> PDF
                </Button>
              </a>
              <a href={d.docx} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="rounded-full gap-1.5" data-testid={`admin-doc-${d.key}-docx`}>
                  <FileText className="h-3.5 w-3.5" /> Word
                </Button>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

